import { useState } from 'react';
import { Link } from 'react-router-dom';
import { mockEditions } from '@/mocks/editions';

interface EditorToolbarProps {
  currentPage: number;
  totalPages: number;
  onNewArticle: () => void;
  onGenerateAI: () => void;
  onPreview: () => void;
  onExportPDF: () => void;
  onAddBlock: (type: string) => void;
  zoom: number;
  onZoomChange: (z: number) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

export default function EditorToolbar({
  onNewArticle,
  onGenerateAI,
  onPreview,
  onExportPDF,
  onAddBlock,
  zoom,
  onZoomChange,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
}: EditorToolbarProps) {
  const [editionOpen, setEditionOpen] = useState(false);
  const [selectedEdition, setSelectedEdition] = useState(mockEditions[0]);

  const zoomLevels = [50, 75, 100, 125, 150];

  return (
    <div className="h-12 bg-white border-b-2 border-[#111111] flex items-center px-4 gap-3 flex-shrink-0 z-30">
      {/* Logo */}
      <Link to="/dashboard" className="flex items-center gap-2 mr-2 cursor-pointer">
        <img
          src="https://static.readdy.ai/image/cb3f25066178f57a7b4ff6ec1e98d674/3adf4e78d4ccc80f9408185bfae7ead8.png"
          alt="JCP"
          className="w-6 h-6 object-contain"
        />
        <span className="font-serif font-bold text-[#111111] text-sm whitespace-nowrap hidden lg:block">Jardel City Post</span>
      </Link>

      <div className="w-px h-6 bg-[#E0E0E0]"></div>

      {/* Edition Selector */}
      <div className="relative">
        <button
          onClick={() => setEditionOpen(!editionOpen)}
          className="flex items-center gap-2 px-3 py-1.5 border border-[#E0E0E0] hover:border-[#111111] transition-colors cursor-pointer text-xs font-sans"
        >
          <i className="ri-newspaper-line text-[#C41230] text-sm"></i>
          <span className="font-semibold text-[#111111] whitespace-nowrap">Edição #{selectedEdition.number}</span>
          <i className="ri-arrow-down-s-line text-[#999999] text-sm"></i>
        </button>
        {editionOpen && (
          <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-[#E0E0E0] shadow-sm z-50">
            {mockEditions.map((ed) => (
              <button
                key={ed.id}
                onClick={() => { setSelectedEdition(ed); setEditionOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#F8F8F8] cursor-pointer transition-colors ${selectedEdition.id === ed.id ? 'bg-[#F0F0F0]' : ''}`}
              >
                <div className="flex-1">
                  <p className="text-xs font-semibold text-[#111111] font-sans">Edição #{ed.number}</p>
                  <p className="text-[10px] text-[#999999] font-sans">{ed.theme}</p>
                </div>
                <span className={`text-[9px] font-bold px-1.5 py-0.5 border font-sans uppercase ${
                  ed.status === 'publicado' ? 'border-green-600 text-green-600' :
                  ed.status === 'em_producao' ? 'border-[#C41230] text-[#C41230]' :
                  'border-[#999999] text-[#999999]'
                }`}>
                  {ed.status === 'publicado' ? 'Pub' : ed.status === 'em_producao' ? 'Prod' : 'Plan'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="w-px h-6 bg-[#E0E0E0]"></div>

      {/* Undo/Redo */}
      <div className="flex items-center gap-1">
        <button
          onClick={onUndo}
          disabled={!canUndo}
          className={`w-7 h-7 flex items-center justify-center transition-colors cursor-pointer ${canUndo ? 'text-[#444444] hover:bg-[#F0F0F0]' : 'text-[#CCCCCC] cursor-not-allowed'}`}
          title="Desfazer"
        >
          <i className="ri-arrow-go-back-line text-sm"></i>
        </button>
        <button
          onClick={onRedo}
          disabled={!canRedo}
          className={`w-7 h-7 flex items-center justify-center transition-colors cursor-pointer ${canRedo ? 'text-[#444444] hover:bg-[#F0F0F0]' : 'text-[#CCCCCC] cursor-not-allowed'}`}
          title="Refazer"
        >
          <i className="ri-arrow-go-forward-line text-sm"></i>
        </button>
      </div>

      <div className="w-px h-6 bg-[#E0E0E0]"></div>

      {/* Add Blocks */}
      <div className="flex items-center gap-1">
        {[
          { type: 'article', icon: 'ri-file-text-line', label: 'Artigo' },
          { type: 'image', icon: 'ri-image-line', label: 'Imagem' },
          { type: 'ad', icon: 'ri-advertisement-line', label: 'Anúncio' },
          { type: 'title', icon: 'ri-heading', label: 'Título' },
          { type: 'quote', icon: 'ri-double-quotes-l', label: 'Citação' },
        ].map((b) => (
          <button
            key={b.type}
            onClick={() => onAddBlock(b.type)}
            title={`Adicionar ${b.label}`}
            className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-sans text-[#444444] hover:bg-[#F0F0F0] hover:text-[#111111] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={`${b.icon} text-sm`}></i>
            <span className="hidden xl:inline">{b.label}</span>
          </button>
        ))}
      </div>

      <div className="w-px h-6 bg-[#E0E0E0]"></div>

      {/* Zoom */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => onZoomChange(Math.max(50, zoom - 25))}
          className="w-6 h-6 flex items-center justify-center text-[#444444] hover:bg-[#F0F0F0] cursor-pointer transition-colors"
        >
          <i className="ri-subtract-line text-xs"></i>
        </button>
        <select
          value={zoom}
          onChange={(e) => onZoomChange(Number(e.target.value))}
          className="text-xs font-sans text-[#111111] border border-[#E0E0E0] px-1 py-0.5 cursor-pointer bg-white w-16"
        >
          {zoomLevels.map((z) => (
            <option key={z} value={z}>{z}%</option>
          ))}
        </select>
        <button
          onClick={() => onZoomChange(Math.min(150, zoom + 25))}
          className="w-6 h-6 flex items-center justify-center text-[#444444] hover:bg-[#F0F0F0] cursor-pointer transition-colors"
        >
          <i className="ri-add-line text-xs"></i>
        </button>
      </div>

      {/* Spacer */}
      <div className="flex-1"></div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        <button
          onClick={onNewArticle}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-[#111111] text-[#111111] text-xs font-sans font-semibold hover:bg-[#F0F0F0] transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line text-sm"></i>
          <span className="hidden sm:inline">Novo Artigo</span>
        </button>
        <button
          onClick={onGenerateAI}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#111111] text-white text-xs font-sans font-semibold hover:bg-[#333333] transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-robot-line text-sm"></i>
          <span className="hidden sm:inline">Gerar com IA</span>
        </button>
        <button
          onClick={onPreview}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-[#E0E0E0] text-[#444444] text-xs font-sans hover:bg-[#F0F0F0] transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-eye-line text-sm"></i>
          <span className="hidden md:inline">Preview</span>
        </button>
        <button
          onClick={onExportPDF}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#C41230] text-white text-xs font-sans font-semibold hover:bg-[#a00e26] transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-file-pdf-line text-sm"></i>
          <span className="hidden md:inline">Exportar PDF</span>
        </button>
      </div>
    </div>
  );
}
